import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Trash2, Edit2, FileText, Youtube, Type, Upload, Link2, ChevronDown, ChevronRight,
  Sparkles, Wand2, Loader2, Plus, ArrowLeft, Video
} from 'lucide-react';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';
import { leitnerService } from '../../services/leitnerService';

const NOTE_TYPES = {
  TEXT: 'text',
  PDF: 'pdf',
  YOUTUBE: 'youtube'
};

const EMPTY_FORM = {
  title: '',
  type: NOTE_TYPES.TEXT,
  text: '',
  url: '',
  subject: '',
  grade: '',
  topic: ''
};

const LeitnerNotesManager = ({ bookId, notes = [], onNotesChange }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [pdfFile, setPdfFile] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  const [search, setSearch] = useState('');
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [isAiMode, setIsAiMode] = useState(false);
  const [aiSourceType, setAiSourceType] = useState('text'); // 'text', 'pdf', 'video'
  const [aiSourceContent, setAiSourceContent] = useState('');
  const [aiPdfFile, setAiPdfFile] = useState(null);
  const [aiVideoFile, setAiVideoFile] = useState(null);
  const [aiVideoUrl, setAiVideoUrl] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  useEffect(() => {
    const loadCategories = async () => {
      if (!bookId) return;
      try {
        const cats = await leitnerService.getQuestionCategories(bookId);
        setCategories({
          subjects: cats.subjects || [],
          grades: cats.grades || [],
          topics: cats.topics || []
        });
      } catch (loadError) {
        console.error('Failed to load note categories:', loadError);
      }
    };
    loadCategories();
  }, [bookId]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setPdfFile(null);
    setEditingNoteId(null);
  };

  const startEdit = (note) => {
    setEditingNoteId(note.id);
    setForm({
      title: note.title || '',
      type: note.type || NOTE_TYPES.TEXT,
      text: note.text || '',
      url: note.url || '',
      subject: note.subject || '',
      grade: note.grade || '',
      topic: note.topic || ''
    });
    setPdfFile(null);
    setError(null);
  };

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((note) => {
      return [
        note.title,
        note.subject,
        note.grade,
        note.topic,
        note.text
      ].some((field) => (field || '').toLowerCase().includes(q));
    });
  }, [notes, search]);

  const handleSave = async () => {
    const title = (form.title || '').trim();
    if (!title) {
      setError('Title is required.');
      return;
    }
    if (!(form.subject || '').trim() || !(form.grade || '').trim() || !(form.topic || '').trim()) {
      setError('Subject, grade, and topic are required.');
      return;
    }
    if (form.type === NOTE_TYPES.TEXT && !(form.text || '').trim()) {
      setError('Text content is required.');
      return;
    }
    if (form.type === NOTE_TYPES.YOUTUBE && !(form.url || '').trim()) {
      setError('YouTube URL is required.');
      return;
    }
    if (form.type === NOTE_TYPES.PDF && !(form.url || '').trim() && !pdfFile) {
      setError('Provide a PDF URL or upload a PDF file.');
      return;
    }

    let resolvedUrl = (form.url || '').trim();
    let resolvedName = '';

    try {
      setSaving(true);
      setError(null);

      if (form.type === NOTE_TYPES.PDF && pdfFile) {
        const uploadResult = await leitnerService.uploadQuestionNotePdf(bookId, pdfFile);
        resolvedUrl = uploadResult.url;
        resolvedName = uploadResult.name || '';
      }

      const payload = {
        title,
        type: form.type,
        text: form.type === NOTE_TYPES.TEXT ? form.text.trim() : '',
        url: (form.type === NOTE_TYPES.PDF || form.type === NOTE_TYPES.YOUTUBE) ? resolvedUrl : '',
        name: form.type === NOTE_TYPES.PDF ? (resolvedName || pdfFile?.name || '') : '',
        subject: form.subject.trim(),
        grade: form.grade.trim(),
        topic: form.topic.trim()
      };

      if (editingNoteId) {
        const updated = await leitnerService.updateLeitnerNote(bookId, editingNoteId, payload);
        onNotesChange(notes.map((note) => (note.id === editingNoteId ? { ...note, ...updated } : note)));
      } else {
        const created = await leitnerService.addLeitnerNote(bookId, payload);
        onNotesChange([{ ...payload, id: created.id }, ...notes]);
      }

      resetForm();
    } catch (saveError) {
      console.error('Failed to save note:', saveError);
      setError('Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await leitnerService.deleteLeitnerNote(bookId, noteId);
      onNotesChange(notes.filter((note) => note.id !== noteId));
    } catch (deleteError) {
      console.error('Failed to delete note:', deleteError);
      setError('Failed to delete note.');
    }
  };

  const handleAiGenerate = async () => {
    try {
      setIsAiGenerating(true);
      setError(null);

      let finalContent = aiSourceContent;
      let finalUrl = '';

      if (aiSourceType === 'pdf') {
        if (!aiPdfFile && !aiSourceContent) {
          setError('Please upload a PDF or provide a URL');
          return;
        }
        if (aiPdfFile) {
          const upload = await leitnerService.uploadQuestionNotePdf(bookId, aiPdfFile);
          finalUrl = upload.url;
          finalContent = `PDF: ${aiPdfFile.name}`;
        }
      } else if (aiSourceType === 'video') {
        if (!aiVideoFile && !aiVideoUrl) {
          setError('Please upload a video or provide a YouTube URL');
          return;
        }
        if (aiVideoFile) {
          const upload = await leitnerService.uploadLeitnerVideo(bookId, aiVideoFile);
          finalUrl = upload.url;
          finalContent = `Video: ${aiVideoFile.name}`;
        } else {
          finalUrl = aiVideoUrl;
          finalContent = `YouTube Video: ${aiVideoUrl}`;
        }
      }

      const results = await leitnerService.processLeitnerNotebook(bookId, finalContent, {
        sourceType: aiSourceType,
        sourceUrl: finalUrl,
        generateNotes: true,
        generateCards: true
      });

      setAiResults(results);
    } catch (genError) {
      console.error('AI Generation failed:', genError);
      setError('AI Generation failed. Please ensure the source is accessible and try again.');
    } finally {
      setIsAiGenerating(false);
    }
  };

  const handleAddAiNote = async (aiNote) => {
    try {
      setSaving(true);
      const payload = {
        title: aiNote.title,
        type: NOTE_TYPES.TEXT,
        text: aiNote.content,
        subject: form.subject || 'AI Generated',
        grade: form.grade || 'AI Generated',
        topic: form.topic || 'AI Generated',
        tags: ['ai-generated']
      };
      const created = await leitnerService.addLeitnerNote(bookId, payload);
      onNotesChange([{ ...payload, id: created.id }, ...notes]);

      // Remove from results to show it's added
      setAiResults(prev => ({
        ...prev,
        notes: prev.notes.filter(n => n !== aiNote)
      }));
    } catch (addError) {
      console.error('Failed to add AI note:', addError);
      setError('Failed to add note to library.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          {isAiMode ? (
            <>
              <Sparkles className="h-6 w-6 text-indigo-400" />
              AI Note Assistant
            </>
          ) : (
            <>
              <BookOpen className="h-6 w-6 text-cyan-400" />
              Leitner Notes
            </>
          )}
        </h3>
        <button
          onClick={() => setIsAiMode(!isAiMode)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${isAiMode
            ? 'bg-slate-800 text-slate-300 hover:text-white border border-slate-700'
            : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-500/20'
            }`}
        >
          {isAiMode ? (
            <>
              <ArrowLeft className="h-4 w-4" />
              Back to Manual
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate with AI
            </>
          )}
        </button>
      </div>

      {isAiMode ? (
        <div className="rounded-xl max-w-5xl mx-auto border border-indigo-500/30 bg-slate-900/50 backdrop-blur-sm p-6 space-y-6 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: 'text', label: 'Paste Text', icon: Type, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
              { id: 'pdf', label: 'Ground via PDF', icon: FileText, color: 'text-rose-400', bg: 'bg-rose-400/10' },
              { id: 'video', label: 'Ground via Video', icon: Video, color: 'text-amber-400', bg: 'bg-amber-400/10' }
            ].map(source => (
              <button
                key={source.id}
                onClick={() => setAiSourceType(source.id)}
                className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${aiSourceType === source.id
                  ? `border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/5`
                  : 'border-slate-700 bg-slate-800/40 hover:bg-slate-800'
                  }`}
              >
                <div className={`p-2 rounded-lg ${source.bg}`}>
                  <source.icon className={`h-5 w-5 ${source.color}`} />
                </div>
                <span className={`font-medium ${aiSourceType === source.id ? 'text-white' : 'text-slate-400'}`}>
                  {source.label}
                </span>
              </button>
            ))}
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            {aiSourceType === 'text' && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-300">Source Text</label>
                <InlineMathField
                  value={aiSourceContent}
                  onChange={setAiSourceContent}
                  rows={8}
                  placeholder="Paste the material you want to turn into notes here..."
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-lg p-4 text-slate-100"
                />
              </div>
            )}

            {aiSourceType === 'pdf' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-700 rounded-xl bg-slate-900/40 hover:border-rose-500/50 transition-colors group">
                  <Upload className="h-10 w-10 text-slate-500 group-hover:text-rose-400 mb-3" />
                  <p className="text-slate-300 font-medium mb-1">
                    {aiPdfFile ? aiPdfFile.name : 'Click to upload source PDF'}
                  </p>
                  <p className="text-xs text-slate-500">Max size 20MB • AI will ground on this content</p>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    id="ai-pdf-upload"
                    onChange={(e) => setAiPdfFile(e.target.files[0])}
                  />
                  <label htmlFor="ai-pdf-upload" className="absolute inset-0 cursor-pointer" />
                </div>
                <div className="text-center text-xs text-slate-500">OR</div>
                <input
                  type="url"
                  placeholder="Paste PDF Link"
                  value={aiSourceContent}
                  onChange={(e) => setAiSourceContent(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-indigo-500 outline-none"
                />
              </div>
            )}

            {aiSourceType === 'video' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-700 rounded-xl bg-slate-900/40 hover:border-amber-500/50 transition-colors group">
                  <Video className="h-10 w-10 text-slate-500 group-hover:text-amber-400 mb-3" />
                  <p className="text-slate-300 font-medium mb-1">
                    {aiVideoFile ? aiVideoFile.name : 'Click to upload video source'}
                  </p>
                  <p className="text-xs text-slate-500">Video transcript will be used for grounding</p>
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    id="ai-video-upload"
                    onChange={(e) => setAiVideoFile(e.target.files[0])}
                  />
                  <label htmlFor="ai-video-upload" className="absolute inset-0 cursor-pointer" />
                </div>
                <div className="text-center text-xs text-slate-500">OR</div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                    <input
                      type="url"
                      placeholder="Paste YouTube Link"
                      value={aiVideoUrl}
                      onChange={(e) => setAiVideoUrl(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-indigo-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={handleAiGenerate}
              disabled={isAiGenerating || (!aiSourceContent && !aiPdfFile && !aiVideoFile && !aiVideoUrl)}
              className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-indigo-600/20"
            >
              {isAiGenerating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating Notes...
                </>
              ) : (
                <>
                  <Wand2 className="h-5 w-5" />
                  Generate Grounded Notes
                </>
              )}
            </button>
          </div>

          {aiResults && (
            <div className="mt-8 space-y-4 border-t border-slate-700 pt-8 animate-in slide-in-from-bottom-4 duration-500">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-400" />
                Generated Library
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiResults.notes?.map((note, idx) => (
                  <div key={`ai-res-${idx}`} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-indigo-500/50 transition-all group shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-0.5 rounded">Note</span>
                      <button
                        onClick={() => handleAddAiNote(note)}
                        className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition shadow-lg shadow-indigo-600/20"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <h5 className="font-bold text-slate-100 mb-2 truncate">{note.title}</h5>
                    <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed mb-4">{note.content}</p>
                    <div className="flex items-center gap-2">
                      {note.topics?.slice(0, 2).map(t => (
                        <span key={t} className="text-[10px] text-slate-500 bg-slate-900 px-2 py-0.5 rounded border border-slate-700">{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-xl max-w-5xl mx-auto border border-slate-700 bg-slate-900 p-4 sm:p-5 space-y-4 shadow-lg">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-cyan-400" />
                Leitner Notes
              </h3>
              <span className="text-xs sm:text-sm text-slate-400">{notes.length} notes</span>
            </div>

            {error && (
              <div className="p-2 rounded bg-red-900/30 border border-red-500/40 text-red-300 text-sm">{error}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Note title"
                className="md:col-span-2 bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              />
              <select
                value={form.type}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, type: e.target.value, text: '', url: '' }));
                  setPdfFile(null);
                }}
                className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              >
                <option value={NOTE_TYPES.TEXT}>Text</option>
                <option value={NOTE_TYPES.PDF}>PDF</option>
                <option value={NOTE_TYPES.YOUTUBE}>YouTube</option>
              </select>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingNoteId ? 'Update Note' : 'Add Note'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                list="note-subject-options"
                type="text"
                value={form.subject}
                onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="Subject"
                className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              />
              <datalist id="note-subject-options">
                {categories.subjects.map((subject) => (
                  <option key={subject} value={subject} />
                ))}
              </datalist>
              <input
                list="note-grade-options"
                type="text"
                value={form.grade}
                onChange={(e) => setForm((prev) => ({ ...prev, grade: e.target.value }))}
                placeholder="Grade"
                className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              />
              <datalist id="note-grade-options">
                {categories.grades.map((grade) => (
                  <option key={grade} value={grade} />
                ))}
              </datalist>
              <input
                list="note-topic-options"
                type="text"
                value={form.topic}
                onChange={(e) => setForm((prev) => ({ ...prev, topic: e.target.value }))}
                placeholder="Topic"
                className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              />
              <datalist id="note-topic-options">
                {categories.topics.map((topic) => (
                  <option key={topic} value={topic} />
                ))}
              </datalist>
            </div>

            {form.type === NOTE_TYPES.TEXT ? (
              <div className="space-y-2">
                <InlineMathField
                  value={form.text}
                  onChange={(text) => setForm((prev) => ({ ...prev, text }))}
                  rows={4}
                  placeholder="Write note text in markdown, with math support..."
                  className="w-full bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
                />
                <div className="rounded-md border border-slate-700 bg-slate-950/70 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Preview</div>
                  <div className="prose prose-sm prose-invert max-w-none text-slate-100">
                    <MathRenderer content={form.text || ''} enableMarkdown={true} enableMath={true} />
                  </div>
                </div>
              </div>
            ) : form.type === NOTE_TYPES.PDF ? (
              <div className="space-y-2">
                <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer text-sm text-slate-300 hover:border-cyan-500">
                  <Upload size={16} className="mr-2" />
                  {pdfFile ? `Selected: ${pdfFile.name}` : 'Upload PDF'}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                  placeholder="Or paste PDF URL"
                  className="w-full bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
                />
              </div>
            ) : (
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="YouTube URL"
                className="w-full bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100"
              />
            )}

            {editingNoteId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-sm text-slate-300 hover:text-white"
              >
                Cancel edit
              </button>
            )}
          </div>

          <div className="rounded-xl max-w-5xl mx-auto border border-slate-700 bg-slate-900 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-200">Library</div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes..."
                className="w-full max-w-xs bg-slate-800 border border-slate-600 rounded-md px-3 py-1.5 text-sm text-slate-100"
              />
            </div>

            {filteredNotes.length === 0 ? (
              <div className="text-center py-10 bg-slate-800/60 rounded-lg text-slate-400">No notes found.</div>
            ) : (
              <div className="space-y-2">
                {filteredNotes.map((note) => {
                  const isExpanded = expandedNotes.has(note.id);
                  return (
                    <div key={note.id} className="border border-slate-700 rounded-lg bg-slate-800/60">
                      <div
                        className="flex items-center justify-between gap-3 p-3 cursor-pointer select-none"
                        onClick={() => setExpandedNotes((prev) => {
                          const next = new Set(prev);
                          next.has(note.id) ? next.delete(note.id) : next.add(note.id);
                          return next;
                        })}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isExpanded ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                          {note.type === NOTE_TYPES.TEXT && <Type size={14} className="text-cyan-400 shrink-0" />}
                          {note.type === NOTE_TYPES.PDF && <FileText size={14} className="text-rose-400 shrink-0" />}
                          {note.type === NOTE_TYPES.YOUTUBE && <Youtube size={14} className="text-red-400 shrink-0" />}
                          <span className="font-semibold text-slate-100 truncate">{note.title}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <div className="hidden sm:flex items-center gap-1 text-[11px] mr-2">
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.subject || 'No subject'}</span>
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.grade || 'No grade'}</span>
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.topic || 'No topic'}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); startEdit(note); }}
                            className="p-2 text-slate-300 hover:bg-slate-700 rounded-md"
                            title="Edit note"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                            className="p-2 text-red-400 hover:bg-red-900/30 rounded-md"
                            title="Delete note"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0 border-t border-slate-700">
                          <div className="flex flex-wrap items-center gap-1 text-[11px] mt-2 sm:hidden">
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.subject || 'No subject'}</span>
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.grade || 'No grade'}</span>
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{note.topic || 'No topic'}</span>
                            <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 uppercase">{note.type}</span>
                          </div>

                          {note.type === NOTE_TYPES.TEXT && (
                            <div className="prose prose-xs prose-invert max-w-none mt-2 text-slate-200">
                              <MathRenderer content={note.text || ''} enableMarkdown={true} enableMath={true} />
                            </div>
                          )}

                          {(note.type === NOTE_TYPES.PDF || note.type === NOTE_TYPES.YOUTUBE) && note.url && (
                            <a
                              href={note.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-400 hover:underline mt-2 inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Link2 size={12} />
                              {note.name || note.url}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LeitnerNotesManager;
